import moment from "moment"
import type {
	JourneyDayItem,
	JourneyDayTripExcursionItem,
	JourneyDayTripItem,
} from "models/axiosModel"
import { toTitleCase } from "util/textcase"
import type {
	IQuotationPreviewV2CostGroup,
	IQuotationPreviewV2CostLine,
	IQuotationPreviewV2DayCost,
} from "./quotationPreviewV2Types"

export const HOTEL_TAX_MULTIPLIER = 1.02

// Amounts are shown exactly as the API sends them (k-VND), rounded to whole numbers.
export const formatAmount = (value: number): string => Math.round(Number(value) || 0).toLocaleString("en-US")

export const formatVnd = (value: number): string => `VND ${formatAmount(value)}`

const toNumber = (value: unknown): number => Number(value) || 0

const toOptionalNumber = (value: unknown): number | null => {
	if (value === null || value === undefined || value === "") return null
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : null
}

const cleanText = (value: unknown): string => String(value ?? "").trim()

export const getMealPaxCount = (quotation: any): number =>
	toNumber(quotation?.number_of_adults) + toNumber(quotation?.number_of_children) + toNumber(quotation?.number_of_extra_bed)

const buildTransportationLines = (trips: JourneyDayTripItem[]): IQuotationPreviewV2CostLine[] =>
	trips.flatMap((trip) => {
		const name = cleanText(trip?.day_trip_name)
		if (!name) return []
		const seater = cleanText(trip?.seating_type_name)
		const seaterSuffix = seater ? ` (${seater})` : ""
		const price = toOptionalNumber(trip?.applied_trip_price) ?? toNumber(trip?.seating_price)
		const lines: IQuotationPreviewV2CostLine[] = [
			{ service: `${toTitleCase(name)}${seaterSuffix}`, calculation: formatVnd(price), amount: price },
		]
		const dinnerPrice = toNumber(trip?.applied_dinner_transport_price)
		if (dinnerPrice > 0) {
			lines.push({ service: `${toTitleCase(name)} Dinner${seaterSuffix}`, calculation: formatVnd(dinnerPrice), amount: dinnerPrice })
		}
		return lines
	})

// "6 Adult × VND 150 + 2 Child × VND 100 = VND 1,100", skipping tickets with quantity 0.
const buildTicketLine = (service: string, tickets: any[] | undefined): IQuotationPreviewV2CostLine => {
	const terms = (Array.isArray(tickets) ? tickets : [])
		.map((ticket) => ({
			quantity: toNumber(ticket?.quantity),
			price: toNumber(ticket?.price),
			label: toTitleCase(cleanText(ticket?.category_name)),
		}))
		.filter((term) => term.quantity > 0)
	const total = terms.reduce((sum, term) => sum + term.quantity * term.price, 0)
	const calculation = terms.length === 0
		? formatVnd(0)
		: `${terms.map((term) => `${term.quantity}${term.label ? ` ${term.label}` : ""} × ${formatVnd(term.price)}`).join(" + ")} = ${formatVnd(total)}`
	return { service, calculation, amount: total }
}

const buildExcursionLine = (excursion: JourneyDayTripExcursionItem): IQuotationPreviewV2CostLine => {
	const service = toTitleCase(cleanText(excursion?.excursion_name)) || "-"
	const boat = excursion?.boat_pricing
	if (!boat) return buildTicketLine(service, excursion?.tickets)

	const total = toNumber(boat.total_cost)
	const numBoats = toOptionalNumber(boat.num_boats)
	const boatPrice = toOptionalNumber(boat.boat_price)
	// Cano / speed boat has no boat count or unit price, only the total.
	const calculation = numBoats != null && boatPrice != null
		? `${numBoats} × ${formatVnd(boatPrice)} = ${formatVnd(total)}`
		: formatVnd(total)
	return { service, calculation, amount: total }
}

// The add-on name is on the saved row; older rows only carry the id, so fall back to the excursion's add-on catalog.
const resolveAddOnName = (addOn: any, excursion: JourneyDayTripExcursionItem): string => {
	const savedName = cleanText(addOn?.add_on_name || addOn?.excursion_add_on_name)
	if (savedName) return savedName
	const catalog = Array.isArray(excursion?.excursion_add_ons) ? excursion.excursion_add_ons : []
	const match = catalog.find((item: any) => String(item?.excursion_add_on_id ?? "") === String(addOn?.excursion_add_on_id ?? ""))
	return cleanText(match?.add_on_name)
}

// Each add-on is its own row right after its excursion, priced from its tickets like a normal excursion.
const buildExcursionLines = (trips: JourneyDayTripItem[]): IQuotationPreviewV2CostLine[] =>
	trips.flatMap((trip) => (Array.isArray(trip?.excursions) ? trip.excursions : []).flatMap((excursion) => [
		buildExcursionLine(excursion),
		...(Array.isArray(excursion?.add_ons) ? excursion.add_ons : []).map((addOn) =>
			buildTicketLine(toTitleCase(resolveAddOnName(addOn, excursion)) || "-", addOn?.tickets),
		),
	]))

const buildMealLines = (day: JourneyDayItem, paxCount: number): IQuotationPreviewV2CostLine[] => {
	const meals = (Array.isArray(day?.meals) ? day.meals : []).filter((meal) => cleanText(meal?.quotation_meal_type_name))
	if (meals.length === 0) return []
	const total = meals.reduce((sum, meal) => sum + toNumber(meal?.price) * paxCount, 0)
	return [{
		service: meals.map((meal) => cleanText(meal.quotation_meal_type_name)).join(" + "),
		calculation: `${meals.map((meal) => `${formatVnd(toNumber(meal?.price))} × ${paxCount} pax`).join(" + ")} = ${formatVnd(total)}`,
		amount: total,
	}]
}

// The tip is per person and only charged when the quotation is "with tip", as in the Journey Details breakdown.
const buildGuideLines = (day: JourneyDayItem, paxCount: number, flagWithTip: boolean): IQuotationPreviewV2CostLine[] => {
	if (!day?.flag_guide) return [{ service: "No", calculation: formatVnd(0), amount: 0 }]
	const basePrice = toNumber(day?.guide_base_price)
	const tipAmount = flagWithTip ? toNumber(day?.guide_tip_amount) : 0
	if (tipAmount <= 0) return [{ service: "Yes", calculation: formatVnd(basePrice), amount: basePrice }]
	const total = basePrice + tipAmount * paxCount
	return [{
		service: "Yes",
		calculation: `${formatVnd(basePrice)} + (${formatVnd(tipAmount)} × ${paxCount} pax) = ${formatVnd(total)}`,
		amount: total,
	}]
}

const hotelChargeLine = (service: string, label: string, unitPrice: number, count: number): IQuotationPreviewV2CostLine => {
	const total = unitPrice * count * HOTEL_TAX_MULTIPLIER
	return {
		service,
		calculation: `${label}: ${formatVnd(unitPrice)} × ${count} × ${HOTEL_TAX_MULTIPLIER} (tax) = ${formatVnd(total)}`,
		amount: total,
	}
}

// Same rules as the Journey Details day breakdown: room, then extra bed / child with bed / child without bed
// when their count is above 0. Children without bed = quotation children minus this hotel's child_with_bed.
const buildHotelLines = (day: JourneyDayItem, childrenCount: number): IQuotationPreviewV2CostLine[] => {
	const hotel = day?.hotel
	const hotelName = cleanText(hotel?.hotel_name)
	if (!hotel || !hotelName) return []
	const roomType = cleanText(hotel.hotel_room_type_name)
	const extraBeds = toNumber(hotel.extra_beds)
	const childWithBed = toNumber(hotel.child_with_bed)
	const childWithoutBed = Math.max(0, childrenCount - childWithBed)
	const lines = [
		hotelChargeLine(
			`${toTitleCase(hotelName)}${roomType ? ` (${toTitleCase(roomType)})` : ""}`,
			"Room",
			toNumber(hotel.room_price),
			toNumber(hotel.rooms),
		),
	]
	if (extraBeds > 0) lines.push(hotelChargeLine("", "Extra Bed", toNumber(hotel.extra_bed_price), extraBeds))
	if (childWithBed > 0) lines.push(hotelChargeLine("", "Child with Bed", toNumber(hotel.child_with_bed_price), childWithBed))
	if (childWithoutBed > 0) lines.push(hotelChargeLine("", "Child without Bed", toNumber(hotel.child_without_bed_price), childWithoutBed))
	return lines
}

const buildDayLabel = (day: JourneyDayItem, index: number): string => {
	const dayText = `Day ${day?.day_number || index + 1}`
	const date = day?.date ? moment(day.date) : null
	return date?.isValid() ? `${dayText} · ${date.format("DD MMM YYYY")}` : dayText
}

// Category order is fixed; a category with nothing on that day is left out.
export const buildDayCosts = (days: JourneyDayItem[], quotation: any): IQuotationPreviewV2DayCost[] => {
	const paxCount = getMealPaxCount(quotation)
	const childrenCount = toNumber(quotation?.number_of_children)
	const flagWithTip = Boolean(quotation?.flag_with_tip)
	return (Array.isArray(days) ? days : []).map((day, index) => {
		const trips = Array.isArray(day?.trips) ? day.trips : []
		const groups: IQuotationPreviewV2CostGroup[] = [
			{ category: "Transportation", lines: buildTransportationLines(trips) },
			{ category: "Excursions", lines: buildExcursionLines(trips) },
			{ category: "Meals", lines: buildMealLines(day, paxCount) },
			{ category: "Guide", lines: buildGuideLines(day, paxCount, flagWithTip) },
			{ category: "Hotel", lines: buildHotelLines(day, childrenCount) },
		].filter((group) => group.lines.length > 0)
		return {
			key: String(day?.quotation_journey_day_id || `day-${index}`),
			label: buildDayLabel(day, index),
			groups,
			total: groups.reduce((sum, group) => sum + group.lines.reduce((lineSum, line) => lineSum + line.amount, 0), 0),
		}
	})
}
